<template>
  <main ref="homePage" class="home-page">
    <section class="home-hero" :aria-label="t('home.highlightsAria')">
      <div ref="heroSwiper" class="home-hero__swiper swiper-container">
        <div class="swiper-wrapper">
          <article
            v-for="(banner, index) in banners"
            :key="banner.id"
            class="swiper-slide home-hero__slide home-hero__slide--media"
            :class="{ 'home-hero__slide--link': Boolean(banner.linkUrl) }"
            :role="banner.linkUrl ? 'link' : undefined"
            :tabindex="banner.linkUrl ? 0 : -1"
            @click="handleBannerClick(banner)"
            @keydown.enter.prevent="handleBannerClick(banner)"
            @keydown.space.prevent="handleBannerClick(banner)"
          >
            <template v-if="isDesktopVideoBanner(banner)">
              <video v-if="!isHeroMobileViewport" class="home-hero__desktop-video home-media" :src="banner.desktopCoverUrl" :aria-label="banner.description || 'ANOVATOR banner'" autoplay :muted="true" loop playsinline preload="metadata" @loadeddata="markMediaReady" @canplay="markMediaReady" />
              <img v-else class="home-hero__mobile-image home-media" :src="banner.mobileCoverUrl" :alt="banner.description || 'ANOVATOR banner'" :loading="isPrimaryHeroBanner(index) ? 'eager' : 'lazy'" :fetchpriority="isPrimaryHeroBanner(index) ? 'high' : null" decoding="async" @load="markMediaReady" @error="markHeroMediaError">
            </template>
            <picture v-else>
              <source media="(max-width: 700px)" :srcset="banner.mobileCoverUrl">
              <img class="home-media" :src="banner.desktopCoverUrl" :alt="banner.description || 'ANOVATOR banner'" :loading="isPrimaryHeroBanner(index) ? 'eager' : 'lazy'" :fetchpriority="isPrimaryHeroBanner(index) ? 'high' : null" decoding="async" @load="markMediaReady" @error="markHeroMediaError">
            </picture>
          </article>
        </div>
        <div ref="heroPagination" class="home-hero__pagination swiper-pagination" />
      </div>
    </section>

    <section id="industries" ref="industriesSection" class="home-industries home-section-reveal home-section-reveal--industries" :aria-label="t('home.industriesAria')">
      <div class="home-industries__inner">
        <article v-for="industry in industries" :key="industry.slot" class="home-industry-card">
          <picture>
            <img class="home-industry-card__image home-media" :src="industry.coverUrl" :alt="industry.title || 'ANOVATOR industry'" loading="lazy" decoding="async" @load="markMediaReady" @error="markMediaReady">
          </picture>
          <div class="home-industry-card__gradient" />
          <div class="home-industry-card__copy">
            <h2>{{ industry.title || 'ANOVATOR' }}</h2>
            <p>{{ industry.description }}</p>
            <router-link class="home-pill-button" :to="industry.link">{{ t('home.learnMore') }}</router-link>
          </div>
        </article>
      </div>
    </section>

    <section ref="technologySection" class="home-technology home-section-reveal home-section-reveal--split" aria-labelledby="technology-title">
      <img class="home-technology__background home-media" src="/static/images/home-v2/technology-mountain.jpg" alt="" aria-hidden="true" loading="lazy" decoding="async" @load="markMediaReady" @error="markMediaReady">
      <div class="home-technology__inner">
        <div class="home-technology__heading home-section-reveal__heading">
          <h2 id="technology-title">{{ t('home.technologyBefore') }} <strong>ANOVATOR</strong> {{ t('home.technologyAfter') }}</h2>
          <router-link class="home-outline-button" to="/about">{{ t('home.learnMore') }}</router-link>
        </div>
        <div class="home-technology__copy home-section-reveal__body">
          <p>{{ t('home.technologyParagraph1') }}</p>
          <p>{{ t('home.technologyParagraph2') }}</p>
          <p>{{ t('home.technologyParagraph3') }}</p>
        </div>
      </div>
    </section>

    <section ref="storiesSection" class="home-stories home-section-reveal home-section-reveal--stories" :aria-label="t('home.featuredStoriesAria')">
      <div ref="storiesSwiper" class="home-stories__swiper">
        <div class="swiper-wrapper">
          <router-link v-for="story in stories" :key="story.position" class="swiper-slide home-story-card" :to="getStoryLink(story)">
            <picture>
              <img v-if="story.coverImage" class="home-media" :src="story.coverImage" :alt="story.title || 'ANOVATOR featured story'" loading="lazy" decoding="async" @load="markMediaReady" @error="markMediaReady">
            </picture>
            <span class="home-story-card__gradient" />
            <h2>{{ story.title || 'ANOVATOR' }}</h2>
          </router-link>
        </div>
      </div>
      <button ref="storiesPrevious" class="legacy-carousel-button home-stories__arrow home-stories__prev" type="button" :aria-label="t('home.previousStory')">
          <span class="home-stories__arrow-icon" aria-hidden="true">
            <img
              class="home-stories__arrow-icon--enabled"
              src="/static/images/home/stories/carousel-arrow-disabled.svg"
              alt=""
              width="40"
              height="40"
            />
            <img
              class="home-stories__arrow-icon--disabled"
              src="/static/images/home/stories/carousel-arrow-enabled.svg"
              alt=""
              width="40"
              height="40"
            />
          </span>
      </button>
      <button ref="storiesNext" class="legacy-carousel-button home-stories__arrow home-stories__next" type="button" :aria-label="t('home.nextStory')">
          <span class="home-stories__arrow-icon" aria-hidden="true">
            <img
              class="home-stories__arrow-icon--enabled"
              src="/static/images/home/stories/carousel-arrow-disabled.svg"
              alt=""
              width="40"
              height="40"
            />
            <img
              class="home-stories__arrow-icon--disabled"
              src="/static/images/home/stories/carousel-arrow-enabled.svg"
              alt=""
              width="40"
              height="40"
            />
          </span>
      </button>
    </section>

    <ExhibitionSchedule ref="exhibitionSection" class="home-section-reveal home-section-reveal--whole" />
    <GlobalFootprint />
  </main>
</template>

<script>
import ExhibitionSchedule from '@/components/ExhibitionSchedule.vue'
import GlobalFootprint from '@/components/GlobalFootprint.vue'
import { bannerAPI, newsAPI, solutionAPI } from '@/utils/api'

const { createHeroSwiperOptions, createStorySwiperOptions } = require('@/utils/home-carousel.cjs')
const { mapHomeIndustryCards, normalizeIndustryCategoriesResponse } = require('@/utils/industries-data.cjs')
const { buildNewsDetailPath, mapHomeFeaturedStories } = require('@/utils/news-list.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')
const { isDesktopMp4Url } = require('@/utils/home-banner-media.cjs')

export default {
  name: 'Home',
  components: { ExhibitionSchedule, GlobalFootprint },
  mixins: [siteLanguageMixin],
  data() {
    return {
      heroSwiper: null,
      storiesCarousel: null,
      sectionRevealObserver: null,
      storiesDesktopMediaQuery: null,
      storiesFourKMediaQuery: null,
      heroMobileMediaQuery: null,
      isHeroMobileViewport: typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches,
      bannerRequestGuard: createLatestSiteRequestGuard(),
      industryRequestGuard: createLatestSiteRequestGuard(),
      featuredStoryRequestGuard: createLatestSiteRequestGuard(),
      banners: [],
      industries: [],
      stories: []
    }
  },
  watch: {
    currentLanguage() {
      this.loadHomeBanners()
      this.loadIndustries()
      this.loadFeaturedStories()
    }
  },
  mounted() {
    document.title = 'ANOVATOR | Smart Body Composition Analyzer'
    this.initHeroMobileMediaQuery()
    this.initStoriesDesktopMediaQuery()
    this.loadHomeBanners()
    this.loadIndustries()
    this.loadFeaturedStories()
    this.$nextTick(this.initSectionReveal)
  },
  beforeUnmount() {
    if (this.heroSwiper) this.heroSwiper.destroy(true, true)
    if (this.storiesCarousel) this.storiesCarousel.destroy(true, true)
    if (this.sectionRevealObserver) this.sectionRevealObserver.disconnect()
    if (this.storiesDesktopMediaQuery) {
      if (this.storiesDesktopMediaQuery.removeEventListener) {
        this.storiesDesktopMediaQuery.removeEventListener('change', this.handleStoriesDesktopMediaChange)
      } else {
        this.storiesDesktopMediaQuery.removeListener(this.handleStoriesDesktopMediaChange)
      }
    }
    if (this.storiesFourKMediaQuery) {
      if (this.storiesFourKMediaQuery.removeEventListener) {
        this.storiesFourKMediaQuery.removeEventListener('change', this.handleStoriesDesktopMediaChange)
      } else {
        this.storiesFourKMediaQuery.removeListener(this.handleStoriesDesktopMediaChange)
      }
    }
    if (this.heroMobileMediaQuery) {
      if (this.heroMobileMediaQuery.removeEventListener) this.heroMobileMediaQuery.removeEventListener('change', this.handleHeroMobileMediaChange)
      else this.heroMobileMediaQuery.removeListener(this.handleHeroMobileMediaChange)
    }
  },
  methods: {
    getSectionRevealElement(reference) {
      if (!reference) return null
      return reference.$el || reference
    },
    observeIndustriesReveal() {
      const industriesTarget = this.$refs.industriesSection
      if (!this.sectionRevealObserver || !industriesTarget || industriesTarget.classList.contains('is-revealed')) return
      this.sectionRevealObserver.observe(industriesTarget)
    },
    initSectionReveal() {
      const homePage = this.$refs.homePage
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (!homePage || prefersReducedMotion || typeof window.IntersectionObserver !== 'function') return

      const targets = [
        this.$refs.technologySection,
        this.$refs.storiesSection,
        this.getSectionRevealElement(this.$refs.exhibitionSection)
      ].filter((target) => target && target.classList)
      const industriesTarget = this.$refs.industriesSection

      if (!targets.length) return

      try {
        this.sectionRevealObserver = new window.IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const shouldReveal = entry.boundingClientRect.top < 0 || (
              entry.isIntersecting && (
                entry.target !== industriesTarget || entry.intersectionRatio >= 0.18
              )
            )
            if (!shouldReveal) return
            entry.target.classList.add('is-revealed')
            this.sectionRevealObserver.unobserve(entry.target)
          })
        }, {
          threshold: [0, 0.18],
          rootMargin: '0px 0px -8% 0px'
        })

        targets.forEach((target) => this.sectionRevealObserver.observe(target))
        if (this.industries.length) this.observeIndustriesReveal()
        homePage.classList.add('reveal-enabled')
      } catch (error) {
        if (this.sectionRevealObserver) this.sectionRevealObserver.disconnect()
        this.sectionRevealObserver = null
      }
    },
    async loadHomeBanners() {
      const request = this.bannerRequestGuard.start(this.currentLanguage)
      try {
        const response = await bannerAPI.getHomeBanners(request.siteType)
        if (!this.bannerRequestGuard.isCurrent(request, this.currentLanguage)) return
        if (!response || response.code !== 0 || !Array.isArray(response.data)) {
          throw new Error((response && response.message) || 'The home banner service returned an invalid response.')
        }
        this.banners = response.data
      } catch (error) {
        if (!this.bannerRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.banners = []
        console.error('Failed to load home banners:', error)
      } finally {
        if (this.bannerRequestGuard.isCurrent(request, this.currentLanguage)) {
          this.$nextTick(this.syncHeroCarousel)
        }
      }
    },
    handleBannerClick(banner) {
      if (!banner || !banner.linkUrl) return
      window.open(banner.linkUrl, '_blank', 'noopener')
    },
    isPrimaryHeroBanner(index) {
      return index === 0
    },
    markHeroSlideReady(media) {
      const slide = media && media.closest('.home-hero__slide--media')
      if (!slide) return
      if (slide.classList.contains('is-hero-media-ready')) return
      slide.classList.remove('has-hero-media-error')
      slide.classList.add('is-hero-media-ready')
    },
    markMediaReady(event) {
      const media = event && (event.currentTarget || event.target)
      if (!media || !media.classList) return
      media.classList.add('is-media-ready')
      this.markHeroSlideReady(media)
      this.syncReadyMediaInSwiper(
        media.closest('.home-hero__swiper, .home-stories__swiper'),
        media
      )
    },
    markHeroMediaError(event) {
      const media = event && (event.currentTarget || event.target)
      const slide = media && media.closest('.home-hero__slide--media')
      if (!slide) return
      media.classList.remove('is-media-ready')
      slide.classList.remove('is-hero-media-ready')
      slide.classList.add('has-hero-media-error')
    },
    normalizeMediaUrl(media) {
      const source = media && (media.currentSrc || media.src)
      if (!source) return ''
      try {
        return new URL(source, window.location.href).href
      } catch (error) {
        return source
      }
    },
    isReadyHomeMedia(media) {
      if (!media) return false
      if (media.tagName === 'IMG') return media.complete && media.naturalWidth > 0
      return media.tagName === 'VIDEO' && media.readyState >= 2
    },
    bindSwiperCloneMediaReady(container) {
      if (!container) return
      container.querySelectorAll('.swiper-slide-duplicate .home-media').forEach((media) => {
        if (media.dataset.homeMediaReadyListener === 'true') return
        media.dataset.homeMediaReadyListener = 'true'
        const handleReady = () => this.markMediaReady({ currentTarget: media })
        media.addEventListener('load', handleReady)
        media.addEventListener('loadeddata', handleReady)
        media.addEventListener('canplay', handleReady)
      })
    },
    syncReadyMediaInSwiper(container, media) {
      if (!container) return
      const source = this.normalizeMediaUrl(media)
      container.querySelectorAll('.home-media').forEach((candidate) => {
        if (source && this.normalizeMediaUrl(candidate) !== source) return
        if (!this.isReadyHomeMedia(candidate)) return
        candidate.classList.add('is-media-ready')
        this.markHeroSlideReady(candidate)
      })
    },
    clearHeroClonePriority() {
      const hero = this.$refs.heroSwiper
      if (!hero) return
      hero.querySelectorAll('.swiper-slide-duplicate [fetchpriority="high"]').forEach((media) => {
        media.removeAttribute('fetchpriority')
      })
    },
    syncHeroVideoMuted() {
      const hero = this.$refs.heroSwiper
      if (!hero) return
      hero.querySelectorAll('.home-hero__desktop-video').forEach((video) => {
        video.muted = true
        video.defaultMuted = true
      })
    },
    isDesktopVideoBanner(banner) {
      return isDesktopMp4Url(banner && banner.desktopCoverUrl)
    },
    initHeroMobileMediaQuery() {
      this.heroMobileMediaQuery = window.matchMedia('(max-width: 700px)')
      this.isHeroMobileViewport = this.heroMobileMediaQuery.matches
      if (this.heroMobileMediaQuery.addEventListener) this.heroMobileMediaQuery.addEventListener('change', this.handleHeroMobileMediaChange)
      else this.heroMobileMediaQuery.addListener(this.handleHeroMobileMediaChange)
    },
    handleHeroMobileMediaChange(event) {
      this.isHeroMobileViewport = event.matches
    },
    async loadIndustries() {
      const request = this.industryRequestGuard.start(this.currentLanguage)
      try {
        const categories = normalizeIndustryCategoriesResponse(
          await solutionAPI.getCategories(request.siteType)
        )
        if (!this.industryRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.industries = mapHomeIndustryCards(categories)
      } catch (error) {
        if (!this.industryRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.industries = []
        console.error('Failed to load home industries:', error)
      } finally {
        if (this.industryRequestGuard.isCurrent(request, this.currentLanguage)) {
          this.$nextTick(this.observeIndustriesReveal)
        }
      }
    },
    async loadFeaturedStories() {
      const request = this.featuredStoryRequestGuard.start(this.currentLanguage)
      try {
        const response = await newsAPI.getFeaturedNews('HOME', request.siteType)
        if (!this.featuredStoryRequestGuard.isCurrent(request, this.currentLanguage)) return
        if (!response || response.code !== 0 || !Array.isArray(response.data)) {
          throw new Error((response && response.message) || 'The featured news service returned an invalid response.')
        }
        this.stories = mapHomeFeaturedStories(response.data)
      } catch (error) {
        if (!this.featuredStoryRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.stories = []
        console.error('Failed to load home featured stories:', error)
      } finally {
        if (this.featuredStoryRequestGuard.isCurrent(request, this.currentLanguage)) {
          this.$nextTick(() => {
            if (this.storiesCarousel) this.storiesCarousel.destroy(true, true)
            this.storiesCarousel = null
            this.syncStoriesCarousel()
          })
        }
      }
    },
    getStoryLink(story) {
      return buildNewsDetailPath(story && story.urlSlug)
    },
    initStoriesDesktopMediaQuery() {
      this.storiesDesktopMediaQuery = window.matchMedia('(min-width: 701px)')
      this.storiesFourKMediaQuery = window.matchMedia('(min-width: 3840px)')
      if (this.storiesDesktopMediaQuery.addEventListener) {
        this.storiesDesktopMediaQuery.addEventListener('change', this.handleStoriesDesktopMediaChange)
        this.storiesFourKMediaQuery.addEventListener('change', this.handleStoriesDesktopMediaChange)
      } else {
        this.storiesDesktopMediaQuery.addListener(this.handleStoriesDesktopMediaChange)
        this.storiesFourKMediaQuery.addListener(this.handleStoriesDesktopMediaChange)
      }
    },
    handleStoriesDesktopMediaChange() {
      if (this.storiesCarousel) this.storiesCarousel.destroy(true, true)
      this.storiesCarousel = null
      this.$nextTick(this.syncStoriesCarousel)
    },
    syncStoriesCarousel() {
      if (!window.Swiper || !this.$refs.storiesSwiper) return
      if (!this.stories.length) {
        if (this.storiesCarousel) this.storiesCarousel.destroy(true, true)
        this.storiesCarousel = null
        return
      }
      if (!this.storiesDesktopMediaQuery.matches) {
        if (this.storiesCarousel) this.storiesCarousel.destroy(true, true)
        this.storiesCarousel = null
        return
      }
      if (this.storiesCarousel) return
      this.storiesCarousel = new window.Swiper(
        this.$refs.storiesSwiper,
        createStorySwiperOptions(
          this.$refs.storiesPrevious,
          this.$refs.storiesNext,
          Boolean(this.storiesFourKMediaQuery && this.storiesFourKMediaQuery.matches)
        )
      )
      this.bindSwiperCloneMediaReady(this.$refs.storiesSwiper)
      this.syncReadyMediaInSwiper(this.$refs.storiesSwiper)
    },
    syncHeroCarousel() {
      if (!window.Swiper || !this.$refs.heroSwiper) return
      if (this.heroSwiper) this.heroSwiper.destroy(true, true)
      this.heroSwiper = null
      if (!this.banners.length) return
      this.heroSwiper = new window.Swiper(
        this.$refs.heroSwiper,
        createHeroSwiperOptions(this.$refs.heroPagination, {
          paginationBulletMessage: this.t('home.goToSlide')
        })
      )
      this.clearHeroClonePriority()
      this.syncHeroVideoMuted()
      this.bindSwiperCloneMediaReady(this.$refs.heroSwiper)
      this.syncReadyMediaInSwiper(this.$refs.heroSwiper)
    }
  }
}
</script>

<style scoped>
.home-page,
.home-page * {
  box-sizing: border-box;
}

.home-page {
  --home-accent: #ef0a6a;
  padding-top: var(--global-header-height, 64px);
  width: 100%;
  overflow: hidden;
  background: #fff;
  color: #000;
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
}

.home-hero {
  width: 100%;
  height: 770px;
  overflow: hidden;
  background: #fff;
}

.home-hero__swiper {
  position: relative;
  width: 100%;
  height: 770px;
  min-height: 0;
  margin: 0 auto;
  overflow: hidden;
}

.home-hero__slide {
  position: relative;
  width: 100%;
  height: 770px;
  overflow: hidden;
  background: #fff;
}

.home-hero__slide--media::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  opacity: 1;
  background: rgba(245, 245, 245, 0.45);
  background-image: linear-gradient(110deg, rgba(245, 245, 245, 0.45) 18%, rgba(255, 255, 255, 0.56) 42%, rgba(245, 245, 245, 0.4) 66%);
  background-size: 220% 100%;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  animation: home-hero-placeholder-flow 1.8s ease-in-out infinite;
  transition: opacity 340ms ease;
}

.home-hero__slide--media.is-hero-media-ready::before {
  opacity: 0;
}

.home-hero__slide--media.has-hero-media-error::before {
  opacity: 1;
  background: rgba(245, 245, 245, 0.45);
  background-image: none;
  animation: none;
  transition: none;
}

@keyframes home-hero-placeholder-flow {
  0%,
  100% { background-position: 100% 50%; }
  50% { background-position: 0 50%; }
}

.home-hero__m9-copy {
  position: absolute;
  top: 104px;
  left: 50%;
  z-index: 3;
  text-align: center;
  transform: translateX(-50%);
}

.home-hero__m9-copy p,
.home-hero__m9-copy h1 {
  margin: 0;
  white-space: nowrap;
}

.home-hero__m9-copy p {
  color: rgba(0, 0, 0, 0.6);
  font-size: 16px;
  line-height: 1.25;
}

.home-hero__m9-copy h1 {
  margin-top: 7px;
  font-family: 'MiSans-Heavy', 'Segoe UI', sans-serif;
  font-size: 30px;
  line-height: 1.2;
}

.home-hero__m9-copy h1 span {
  color: var(--home-accent);
}

.home-hero__m9-background {
  position: absolute;
  top: 151px;
  left: 50%;
  z-index: 1;
  color: transparent;
  background: linear-gradient(180deg, #8c9099 20%, rgba(255, 255, 255, 0) 88%);
  background-clip: text;
  -webkit-background-clip: text;
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 427px;
  line-height: 1;
  transform: translateX(-50%);
  user-select: none;
}

.home-hero__m9-product {
  position: absolute;
  top: 246px;
  left: 50%;
  z-index: 2;
  display: block;
  width: 338px;
  height: 664px;
  max-width: none;
  object-fit: cover;
  transform: translateX(-50%);
}

.home-hero__slide--media > picture,
.home-hero__slide--media > picture > img,
.home-hero__video,
.home-hero__desktop-video,
.home-hero__mobile-image {
  display: block;
  width: 100%;
  height: 100%;
  max-width: none;
  object-fit: cover;
  object-position: center;
}

.home-media {
  opacity: 0;
  transition: opacity 0.24s ease;
}

.home-media.is-media-ready {
  opacity: 1;
}

.home-page.reveal-enabled .home-section-reveal--whole,
.home-page.reveal-enabled .home-section-reveal--split .home-section-reveal__heading,
.home-page.reveal-enabled .home-section-reveal--split .home-section-reveal__body {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 620ms ease-out, transform 620ms ease-out;
}

.home-page.reveal-enabled .home-section-reveal--whole:not(.is-revealed),
.home-page.reveal-enabled .home-section-reveal--split:not(.is-revealed) .home-section-reveal__heading,
.home-page.reveal-enabled .home-section-reveal--split:not(.is-revealed) .home-section-reveal__body {
  opacity: 0;
  transform: translateY(30px);
}

.home-page.reveal-enabled .home-section-reveal--split .home-section-reveal__body {
  transition-delay: 140ms;
}

@media (min-width: 701px) {
  .home-page.reveal-enabled .home-section-reveal--industries .home-industry-card,
  .home-page.reveal-enabled .home-section-reveal--stories .home-story-card {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 620ms ease-out, transform 620ms ease-out;
  }

  .home-page.reveal-enabled .home-section-reveal--industries:not(.is-revealed) .home-industry-card,
  .home-page.reveal-enabled .home-section-reveal--stories:not(.is-revealed) .home-story-card {
    opacity: 0;
    transform: translateY(30px);
  }

  .home-page.reveal-enabled .home-section-reveal--industries .home-industry-card:nth-child(2),
  .home-page.reveal-enabled .home-section-reveal--stories .home-story-card:nth-child(2) {
    transition-delay: 80ms;
  }

  .home-page.reveal-enabled .home-section-reveal--industries .home-industry-card:nth-child(3),
  .home-page.reveal-enabled .home-section-reveal--stories .home-story-card:nth-child(3) {
    transition-delay: 160ms;
  }
}

.home-hero__slide--dark {
  background: #000;
}

.home-hero__video--mobile {
  display: none;
}

.home-hero__mobile-image {
  display: none;
}

.home-hero__pagination {
  bottom: 30px !important;
  left: 50% !important;
  z-index: 8;
  display: flex;
  width: auto !important;
  gap: 16px;
  transform: translateX(-50%);
}

:deep(.home-hero__pagination .swiper-pagination-bullet) {
  width: 8px;
  height: 8px;
  margin: 0 !important;
  background: rgba(0, 0, 0, 0.3);
  opacity: 1;
}

:deep(.home-hero__pagination .swiper-pagination-bullet-active) {
  background: var(--home-accent);
}

@media (min-width: 701px) {
  :deep(.home-hero__pagination .swiper-pagination-bullet) {
    width: 8px !important;
    height: 8px !important;
    border-radius: 50% !important;
    background: rgba(0, 0, 0, 0.3) !important;
    opacity: 1 !important;
  }

  :deep(.home-hero__pagination .swiper-pagination-bullet-active) {
    background: #ef0a6a !important;
  }
}

.home-industries {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 660px;
  padding: 120px 0;
  background: #fff;
  scroll-margin-top: var(--global-header-height, 64px);
}

.home-industries__inner {
  display: grid;
  width: min(calc(100vw - 144px), 1296px);
  max-width: 1296px;
  margin: 0 0 0 max(72px, calc((100vw - 1296px) / 2));
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.home-industry-card {
  position: relative;
  height: 420px;
  min-width: 0;
  overflow: hidden;
  background: #f5f5f7;
}

.home-industry-card__image,
.home-industry-card__gradient {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.home-industry-card__image {
  display: block;
  max-width: none;
  object-fit: cover;
  transition: transform 0.45s ease;
}

.home-industry-card:nth-child(1) .home-industry-card__image {
  object-position: 38% center;
}

.home-industry-card:nth-child(3) .home-industry-card__image {
  object-position: center 28%;
}

.home-industry-card__gradient {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 32%, rgba(255, 255, 255, 0.9) 72%, #fff 100%);
}

.home-industry-card:nth-child(2) .home-industry-card__gradient {
  background: linear-gradient(180deg, rgba(255, 240, 230, 0) 32%, rgba(255, 240, 230, 0.9) 72%, #fff0e6 100%);
}

.home-industry-card:nth-child(3) .home-industry-card__gradient {
  background: linear-gradient(180deg, rgba(227, 238, 255, 0) 32%, rgba(227, 238, 255, 0.9) 72%, #e3eeff 100%);
}

.home-industry-card__copy {
  position: absolute;
  right: 30px;
  bottom: 30px;
  left: 30px;
  z-index: 2;
}

.home-industry-card h2,
.home-industry-card p {
  margin: 0;
}

.home-industry-card h2 {
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 28px;
  line-height: 1.05;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: normal;
}

.home-industry-card p {
  min-height: 54px;
  margin-top: 10px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 1.3;
}

.home-pill-button,
.home-outline-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  margin-top: 11px;
  padding: 0 14px;
  border-radius: 67px;
  font-size: 10px;
  line-height: 1;
  text-decoration: none;
  text-transform: capitalize;
}

.home-pill-button {
  color: #fff;
  background: var(--home-accent);
}

.home-pill-button:hover,
.home-pill-button:focus-visible {
  background: #be0955;
}

.home-industry-card:hover .home-industry-card__image {
  transform: scale(1.03);
}

.home-technology {
  position: relative;
  width: 100%;
  height: 449px;
  overflow: hidden;
  background: #222;
  color: #fff;
}

.home-technology__background {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  max-width: none;
  object-fit: cover;
  object-position: center 58%;
  opacity: 0.2;
}

.home-technology__background.is-media-ready {
  opacity: 0.2;
}

.home-technology__inner {
  position: relative;
  z-index: 1;
  display: grid;
  width: calc(100% - 144px);
  max-width: 1296px;
  height: 100%;
  margin: 0 auto;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  align-items: start;
}

@media (min-width: 1024px) {
  .home-technology__heading,
  .home-technology__copy {
    margin-top: 120px;
  }

  .home-technology__heading h2 {
    line-height: 1.2;
  }

  .home-technology__heading .home-outline-button {
    margin-top: 32px;
  }
}

.home-technology__heading h2 {
  max-width: 633px;
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'MiSans-Regular', sans-serif;
  font-size: 30px;
  font-weight: 400;
  line-height: 1.25;
  text-transform: uppercase;
}

.home-technology__heading strong {
  color: #fff;
  font-family: 'MiSans-Bold', sans-serif;
}

.home-outline-button {
  margin-top: 36px;
  color: #fff;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.55);
}

.home-outline-button:hover,
.home-outline-button:focus-visible {
  opacity: 0.6;
}

.home-technology__copy {
  color: #fff;
  font-size: 14px;
  line-height: 1.35;
  text-align: left;
}

.home-technology__copy p {
  margin: 0;
  font-size: 14px;
  line-height: 1.35;
}

.home-technology__copy p + p {
  margin-top: 12px;
}

.home-stories {
  position: relative;
  width: 100%;
  height: 816px;
  padding: 120px 0;
  overflow: hidden;
  background: #fff;
}

.home-stories__swiper {
  width: 100%;
  height: 576px;
  overflow: visible;
}

.home-story-card {
  position: relative;
  display: block;
  flex: 0 0 1186px;
  width: 1186px;
  max-width: 1186px;
  height: 576px;
  overflow: hidden;
  border-radius: 28px;
  background: #f5f5f7;
  text-decoration: none;
}

.home-story-card img,
.home-story-card__gradient {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.home-story-card img {
  display: block;
  max-width: none;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.home-story-card__gradient {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 46%, rgba(0, 0, 0, 0.88) 100%);
}

.home-story-card h2 {
  position: absolute;
  right: 31px;
  bottom: 30px;
  left: 31px;
  z-index: 1;
  max-width: calc(100% - 62px);
  min-width: 0;
  margin: 0;
  color: #fff;
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 28px;
  line-height: 1.25;
  text-align: left;
  overflow-wrap: anywhere;
  word-break: normal;
}

.home-story-card:hover img {
  transform: scale(1.02);
}

.home-stories__arrow {
  position: absolute;
  top: 50%;
  z-index: 5;
  width: 40px;
  height: 40px;
  padding: 0;
  background: transparent;
  border: 0;
  transform: translateY(-50%);
}

.home-stories__arrow-icon {
  display: block;
  width: 40px;
  height: 40px;
}

.home-stories__arrow-icon img {
  display: block;
  width: 40px;
  height: 40px;
  max-width: none;
}

.home-stories__arrow-icon--disabled {
  display: none !important;
}

.home-stories__prev .home-stories__arrow-icon { transform: rotate(-90deg) scaleY(-1); }
.home-stories__next .home-stories__arrow-icon { transform: rotate(-90deg); }

.home-stories__arrow:hover:not(:disabled):not(.is-disabled):not(.is-locked),
.home-stories__arrow:active:not(:disabled):not(.is-disabled):not(.is-locked),
.home-stories__arrow:focus-visible:not(:disabled):not(.is-disabled):not(.is-locked) {
  background: transparent;
  border-color: transparent;
  color: inherit;
  opacity: 0.82;
}

.home-stories__arrow:focus-visible {
  outline: 2px solid rgba(0, 0, 0, 0.6);
  outline-offset: 3px;
}

.home-stories__arrow:disabled,
.home-stories__arrow.is-disabled,
.home-stories__arrow.is-locked {
  background: transparent;
  border-color: transparent;
  opacity: 1;
}

.home-stories__arrow:disabled .home-stories__arrow-icon,
.home-stories__arrow.is-disabled .home-stories__arrow-icon,
.home-stories__arrow.is-locked .home-stories__arrow-icon {
  opacity: 1;
}

.home-stories__arrow:disabled .home-stories__arrow-icon--enabled,
.home-stories__arrow.is-disabled .home-stories__arrow-icon--enabled,
.home-stories__arrow.is-locked .home-stories__arrow-icon--enabled {
  display: none;
}

.home-stories__arrow:disabled .home-stories__arrow-icon--disabled,
.home-stories__arrow.is-disabled .home-stories__arrow-icon--disabled,
.home-stories__arrow.is-locked .home-stories__arrow-icon--disabled {
  display: block !important;
}

.home-stories__prev {
  left: max(16px, calc(50% - 573px));
}

.home-stories__next {
  right: max(16px, calc(50% - 573px));
}

@media (min-width: 2000px) {
  .home-stories__prev {
    left: calc(50% - 573px);
  }

  .home-stories__next {
    right: calc(50% - 573px);
  }
}

@media (min-width: 3840px) {
  .home-hero,
  .home-hero__swiper,
  .home-hero__slide {
    height: calc(100vw * 770 / 1440);
  }

  .home-industries {
    margin-bottom: 93px;
  }

  .home-stories__swiper {
    width: 3606px;
    margin: 0 auto;
    overflow: hidden;
  }
}

@media (max-width: 1023px) {
  .home-industries,
  .home-stories {
    padding-top: 80px;
    padding-bottom: 80px;
  }

  .home-industries {
    min-height: 0;
  }

  .home-industries__inner,
  .home-technology__inner {
    width: calc(100% - 48px);
    margin-right: auto;
    margin-left: auto;
  }

  .home-industries__inner {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .home-industry-card:last-child {
    grid-column: 1 / -1;
    width: calc(50% - 12px);
    justify-self: center;
  }

  .home-technology {
    height: auto;
    min-height: 449px;
    padding: 72px 0;
  }

  .home-technology__inner {
    height: auto;
  }

  .home-stories {
    height: 696px;
  }

  .home-stories__swiper,
  .home-story-card {
    height: 536px;
  }

  .home-story-card {
    flex-basis: calc(100vw - 96px);
    width: calc(100vw - 96px);
  }
}

@media (max-width: 700px) {
  .home-page.reveal-enabled .home-section-reveal--whole,
  .home-page.reveal-enabled .home-section-reveal--industries,
  .home-page.reveal-enabled .home-section-reveal--stories,
  .home-page.reveal-enabled .home-section-reveal--split .home-section-reveal__heading,
  .home-page.reveal-enabled .home-section-reveal--split .home-section-reveal__body {
    transition-duration: 560ms;
  }

  .home-page.reveal-enabled .home-section-reveal--whole:not(.is-revealed),
  .home-page.reveal-enabled .home-section-reveal--industries:not(.is-revealed),
  .home-page.reveal-enabled .home-section-reveal--stories:not(.is-revealed),
  .home-page.reveal-enabled .home-section-reveal--split:not(.is-revealed) .home-section-reveal__heading,
  .home-page.reveal-enabled .home-section-reveal--split:not(.is-revealed) .home-section-reveal__body {
    transform: translateY(20px);
  }

  .home-hero,
  .home-hero__swiper,
  .home-hero__slide {
    height: calc(100vw * 4 / 3);
  }

  .home-hero__m9-copy {
    top: 74px;
    width: calc(100% - 32px);
  }

  .home-hero__m9-copy p {
    font-size: 12px;
    line-height: 15px;
  }

  .home-hero__m9-copy h1 {
    margin-top: 9px;
    font-size: 24px;
    line-height: 29px;
  }

  .home-hero__m9-background {
    top: 176px;
    font-size: 264px;
    line-height: 0.78;
  }

  .home-hero__m9-product {
    top: 210px;
    width: 231px;
    height: auto;
    aspect-ratio: 2086 / 4096;
    object-fit: contain;
  }

  .home-hero__video--desktop {
    display: none;
  }

  .home-hero__desktop-video {
    display: none;
  }

  .home-hero__video--mobile {
    display: block;
  }

  .home-hero__mobile-image {
    display: block;
  }

  .home-hero__slide--media > picture > img,
  .home-hero__video,
  .home-hero__desktop-video,
  .home-hero__mobile-image {
    object-fit: contain;
    background: #fff;
  }

  .home-hero__pagination {
    bottom: 24px !important;
  }

  :deep(.home-hero__pagination .swiper-pagination-bullet) {
    width: 8px !important;
    height: 8px !important;
  }

  .home-industries {
    padding: 39px 0 80px;
    scroll-margin-top: 50px;
  }

  .home-industries__inner,
  .home-technology__inner {
    width: calc(100% - 32px);
  }

  .home-industries__inner {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .home-industry-card,
  .home-industry-card:last-child {
    width: 100%;
    height: auto;
    min-height: 0;
    aspect-ratio: 1;
    grid-column: auto;
  }

  .home-industry-card__copy {
    right: 30px;
    bottom: 30px;
    left: 30px;
  }

  .home-industry-card h2 {
    min-height: 0;
    font-size: 26px;
    line-height: 1.08;
  }

  .home-industry-card p {
    min-height: 0;
    margin-top: 10px;
    font-size: 13px;
    line-height: 1.3;
  }

  .home-pill-button,
  .home-outline-button {
    height: 28px;
    margin-top: 10px;
    padding: 0 13px;
  }

  .home-industry-card:nth-child(1) .home-industry-card__image {
    object-position: 58% center;
  }

  .home-industry-card:nth-child(2) .home-industry-card__image {
    object-position: center;
  }

  .home-industry-card:nth-child(3) .home-industry-card__image {
    object-position: center 42%;
  }

  .home-technology {
    height: 588px;
    min-height: 588px;
    padding: 0;
  }

  .home-technology__inner {
    display: block;
    height: 100%;
  }

  .home-technology__heading {
    padding-top: 56px;
  }

  .home-technology__heading h2 {
    width: 100%;
    font-size: 24px;
    line-height: 1.25;
  }

  .home-outline-button {
    margin-top: 60px;
  }

  .home-technology__copy {
    margin-top: 48px;
    font-size: 13px;
    line-height: 1.5;
    text-align: left;
  }

  .home-technology__copy p {
    font-size: 13px;
    line-height: 1.5;
  }

  .home-technology__copy p + p {
    margin-top: 12px;
  }

  .home-stories {
    height: auto;
    padding: 80px 16px 0;
  }

  .home-stories__swiper {
    width: 100%;
    height: auto;
    overflow: visible;
  }

  .home-stories .swiper-wrapper {
    display: grid;
    width: 100%;
    height: auto;
    gap: 16px;
    transform: none !important;
  }

  .home-story-card {
    flex: none;
    width: 100%;
    max-width: 400px;
    height: auto;
    aspect-ratio: 1;
    border-radius: 10px;
  }

  .home-story-card h2 {
    right: 30px;
    bottom: 29px;
    left: 30px;
    max-width: calc(100% - 60px);
    font-size: 18px;
    line-height: 1.25;
  }

  .home-stories__arrow {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-hero__slide--media::before {
    background: rgba(245, 245, 245, 0.45);
    background-image: none;
    animation: none;
    transition: none;
  }

  .home-page.reveal-enabled .home-section-reveal,
  .home-page.reveal-enabled .home-section-reveal .home-section-reveal__heading,
  .home-page.reveal-enabled .home-section-reveal .home-section-reveal__body {
    opacity: 1;
    transform: none;
    transition: none;
  }

  .home-page.reveal-enabled .home-section-reveal--industries .home-industry-card,
  .home-page.reveal-enabled .home-section-reveal--stories .home-story-card {
    opacity: 1;
    transform: none;
    transition: none;
    transition-delay: 0;
  }

  .home-industry-card__image,
  .home-story-card img,
  .home-media {
    transition: none;
  }
}
</style>
